// See @web-monorepo/translation-safe-dom
import { JsonObject } from "type-fest";

type FunctionKeysOf<T> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- no unknown-based alternative: (...args: unknown[]) => unknown breaks variance for DOM method types
  [K in keyof T]: T[K] extends (...args: any[]) => any ? K : never;
}[keyof T];

class AnnotatedDOMError extends Error {
  cause: Error | unknown;
  extra: JsonObject;
  tags: Record<string, string>;
  fingerprint: string[];

  constructor(
    public originalError: unknown,
    public methodName: string | symbol,
    public target: unknown,
    public args: unknown[],
  ) {
    super(originalError instanceof Error ? originalError.message : String(originalError));
    this.name = "AnnotatedDOMError";

    if (originalError instanceof Error && originalError.stack) {
      this.stack = originalError.stack;
    }

    this.originalError = originalError;
    this.cause = originalError;
    this.extra = {
      callee: describe(target),
      args: args.map(describe),
    };
    this.tags = {
      domMethod: String(methodName),
    };
    this.fingerprint = [
      "dom-manipulation-error",
      String(methodName),
      target instanceof Node
        ? ancestorDescriptors(target, { includeUnstable: false }).join(" > ")
        : "unknown ancestors",
    ];
  }
}

function describe(maybeNode: Node | unknown): string {
  if (maybeNode instanceof Node) {
    // Full detail on purpose: extra is diagnostic context, not grouping input.
    return `${ancestorDescriptors(maybeNode).join(" > ")} : "${maybeNode.textContent?.slice(0, 100) ?? String(maybeNode)}"`;
  }

  return String(maybeNode);
}

/**
 * Describes each ancestor of `node`, outermost first.
 *
 * `includeUnstable` controls classList and aria-label. Both are build-unstable
 * and can carry user-authored text, so fingerprints must omit them; diagnostic
 * `extra` fields keep the full detail.
 */
export function ancestorDescriptors(node: Node, options: { includeUnstable?: boolean } = {}): string[] {
  const { includeUnstable = true } = options;
  const names = [];
  for (let ancestor = node as Node | null; ancestor; ancestor = ancestor.parentElement) {
    if (ancestor instanceof Element) {
      const name = ancestor.getAttribute("data-name");
      const role = ancestor.getAttribute("role");
      const parts = [];
      parts.push(ancestor.nodeName.toLowerCase());
      if (includeUnstable) {
        if (ancestor.classList.length > 0) {
          parts.push(`.${[...ancestor.classList.values()].join(".")}`);
        }
      }
      if (name) parts.push(`[data-name=${name}]`);
      if (role) parts.push(`[role=${role}]`);
      if (includeUnstable) {
        const ariaLabel = ancestor.getAttribute("aria-label");
        if (ariaLabel) parts.push(`[aria-label=${ariaLabel}]`);
      }
      names.unshift(parts.join(""));
    }
  }
  return names;
}

function annotateErrors<T>(object: T, key: FunctionKeysOf<T>) {
  const original = object[key] as (...args: unknown[]) => unknown;

  (object as unknown as Record<PropertyKey, unknown>)[key as PropertyKey] = function (...args: unknown[]) {
    try {
      return original.call(this, ...args);
    } catch (x) {
      const annotated = new AnnotatedDOMError(x, key as string, this, args);
      // Try to do it using the new beforeSend mechanism:
      // captureException(annotated, { extra: { target: this, args } });
      throw annotated;
    }
  };
}

if (typeof Node === "function" && Node.prototype) {
  annotateErrors(Node.prototype, "removeChild");
  annotateErrors(Node.prototype, "insertBefore");
  annotateErrors(Node.prototype, "appendChild");
}
